/**
 * Maps URL path prefixes to required RBAC permissions for full-page access.
 * Aligns with `navData.tsx` + extra routes not listed in the sidebar.
 * Longest prefix wins (e.g. /campaigns/informed-delivery before /campaigns).
 */

import { navData } from '@/app/components/navData';
import type { NavItem } from '@/lib/filterNavByPermission';
import { APP_BASE_PATH, collapseDuplicateAppBasePath } from '@/lib/appBasePath';

export type RoutePermissionRule = { path: string; permission: string };

function collectNavRules(items: readonly NavItem[], rules: RoutePermissionRule[]): void {
  for (const item of items) {
    const p = item.path;
    if (typeof p === 'string' && p.startsWith('/') && typeof item.permission === 'string') {
      rules.push({ path: p, permission: item.permission });
    }
    for (const r of item.routes ?? []) rules.push({ path: r.path, permission: r.permission });
    if (item.children?.length) collectNavRules(item.children, rules);
  }
}

function flattenNavDataRules(): RoutePermissionRule[] {
  const rules: RoutePermissionRule[] = [];
  for (const group of navData) collectNavRules(group.items, rules);
  return rules;
}

/**
 * Stricter rules for authoring / mutate flows. Merged last so they win over parent
 * nav paths (e.g. /campaigns → campaign.view must not grant /campaigns/create-...).
 */
const ACTION_ROUTE_RULES: RoutePermissionRule[] = [
  { path: '/ads/new-ad-campaign', permission: 'campaign.create' },
  { path: '/ads/edit', permission: 'campaign.edit' },
  { path: '/email/new-email-campaign', permission: 'campaign.create' },
  { path: '/email/edit', permission: 'campaign.edit' },
  { path: '/email/templates/create-new-template', permission: 'template.manage' },
  { path: '/email/templates/edit', permission: 'template.manage' },
  { path: '/campaigns/create-direct-mail-campaign', permission: 'campaign.create' },
  {
    path: '/campaigns/informed-delivery/create-informed-delivery',
    permission: 'campaign.create',
  },
  { path: '/campaigns/edit', permission: 'campaign.edit' },
  { path: '/campaigns/configure-informed-delivery', permission: 'campaign.edit' },
  { path: '/campaigns/proof', permission: 'campaign.edit' },
  { path: '/campaigns/markup', permission: 'campaign.edit' },
  { path: '/campaigns/informed-delivery/edit', permission: 'campaign.edit' },
  { path: '/eddm-campaigns/create-eddm-campaign', permission: 'campaign.create' },
  { path: '/qr-code/create', permission: 'campaign.create' },
  { path: '/qr-code/edit', permission: 'campaign.edit' },
  { path: '/dm/new', permission: 'campaign.create' },
  { path: '/journeys/create-new-journey', permission: 'journey.view' },
  { path: '/automation-flows/create', permission: 'journey.create' },
  { path: '/automation-flows/edit', permission: 'journey.view' },
  { path: '/voice-workflows/create', permission: 'voice.workflow.create' },
  { path: '/logic/new-logic-file', permission: 'journey.create' },
  { path: '/logic/edit', permission: 'journey.edit' },
  { path: '/logic/edit-logic-file', permission: 'journey.edit' },
  { path: '/cdm/build-audience', permission: 'audience.create' },
  { path: '/cdm/upload-audience', permission: 'audience.import' },
  { path: '/cdm/lookalike', permission: 'audience.create' },
  { path: '/cdm/identified-from-anonymous', permission: 'identity_resolution.use' },
  { path: '/creatives/upload', permission: 'asset.create' },
  { path: '/creatives/build', permission: 'asset.create' },
  { path: '/creatives/edit', permission: 'asset.edit' },
  { path: '/predictions/new-prediction-model', permission: 'audience.create' },
  { path: '/account/brands/new', permission: 'settings.manage' },
  { path: '/account/brands/edit', permission: 'settings.manage' },
  { path: '/design', permission: 'asset.edit' },
  { path: '/image-editor', permission: 'asset.edit' },
  { path: '/editor', permission: 'asset.edit' },
];

/**
 * Routes not fully covered by nav paths (or need explicit rules).
 * Same permission philosophy as sidebar / docs.
 */
const EXTRA_ROUTE_RULES: RoutePermissionRule[] = [
  // HALO channels base routes (children in navData inherit campaign.view conceptually)
  { path: '/campaigns', permission: 'campaign.view' },
  { path: '/email', permission: 'campaign.view' },
  { path: '/ads', permission: 'campaign.view' },
  { path: '/eddm-campaigns', permission: 'campaign.view' },
  { path: '/qr-code', permission: 'campaign.view' },
  // CDM hub + common subpages
  { path: '/cdm', permission: 'audience.view' },
  { path: '/cdm/visualize', permission: 'audience.view' },
  { path: '/cdm/details', permission: 'audience.view' },
  { path: '/cdm/lookalikes', permission: 'audience.view' },
  { path: '/executive', permission: 'report.view' },
  { path: '/analytics', permission: 'report.view' },
  { path: '/dm', permission: 'campaign.view' },
  { path: '/release-notes', permission: 'report.view' },
  { path: '/products', permission: 'campaign.view' },
  { path: '/design', permission: 'asset.view' },
  { path: '/image-editor', permission: 'asset.view' },
  { path: '/editor', permission: 'asset.view' },
  { path: '/editor-demo', permission: 'asset.view' },
  { path: '/schedule', permission: 'campaign.view' },
  { path: '/service', permission: 'settings.view' },
  { path: '/tiers', permission: 'subscription.view' },
  { path: '/pricing', permission: 'billing.view' },
  { path: '/quote', permission: 'billing.view' },
  { path: '/transaction-status', permission: 'billing.view' },
  { path: '/qr-preview', permission: 'campaign.view' },
  // Account (tenant) — match account drawer / sensible defaults; personal routes are unlisted → no gate
  { path: '/account/brands', permission: 'settings.view' },
  { path: '/account/subscription', permission: 'subscription.view' },
  { path: '/account/organization', permission: 'member.view' },
  { path: '/account/integration', permission: 'integration.view' },
  { path: '/integrations', permission: 'integration.view' },
  { path: '/account/wallet', permission: 'billing.view' },
  { path: '/account/usage', permission: 'billing.view' },
  { path: '/account/active-orders', permission: 'billing.view' },
  { path: '/account/past-orders', permission: 'billing.view' },
  { path: '/account/saved-cards', permission: 'billing.view' },
  { path: '/account/impersonation-logs', permission: 'audit.view' },
  { path: '/admin', permission: 'settings.manage' },
];

function mergeRules(): RoutePermissionRule[] {
  const fromNav = flattenNavDataRules();
  const byPath = new Map<string, string>();
  for (const r of fromNav) {
    byPath.set(r.path, r.permission);
  }
  // Extras override nav if same path (should be rare)
  for (const r of EXTRA_ROUTE_RULES) {
    byPath.set(r.path, r.permission);
  }
  // Authoring routes override broader paths (e.g. asset.edit for /design vs asset.view)
  for (const r of ACTION_ROUTE_RULES) {
    byPath.set(r.path, r.permission);
  }
  return Array.from(byPath.entries()).map(([path, permission]) => ({ path, permission }));
}

/** Longest path first so /foo/bar wins over /foo */
const SORTED_RULES: RoutePermissionRule[] = mergeRules().sort((a, b) => b.path.length - a.path.length);

/**
 * Returns required permission for this pathname, or null if no RBAC gate applies.
 */
export function getRequiredPermissionForPath(pathname: string): string | null {
  if (!pathname || pathname === '/') return null;
  let path = pathname.split('?')[0];
  // `usePathname()` is basePath-stripped in most setups; normalize if a raw `/v2/...` slips through.
  if (path === APP_BASE_PATH || path.startsWith(`${APP_BASE_PATH}/`)) {
    path = path === APP_BASE_PATH ? '/' : path.slice(APP_BASE_PATH.length) || '/';
    if (!path.startsWith('/')) path = `/${path}`;
  }
  path = collapseDuplicateAppBasePath(path);
  // Main app dashboard: never route-gate (users may only have partial access; page/API enforce details).
  if (path === '/dashboard' || path.startsWith('/dashboard/')) {
    return null;
  }
  for (const rule of SORTED_RULES) {
    if (path === rule.path || path.startsWith(`${rule.path}/`)) {
      return rule.permission;
    }
  }
  return null;
}
